import { Link } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import SeoHead from "../components/SeoHead";
import tokenomicsShellStyle from "../styles/tokenomicsShellStyle";

const PrivacyPolicy = () => {
  return (
    <div className="tokenomics-shell" style={tokenomicsShellStyle}>
      <SeoHead
        title="Privacy Policy | Atheriam"
        description="Learn how Atheriam collects, uses, and protects your information across our website, blockchain services, gaming experiences, and $ATH ecosystem."
        path="/privacy"
      />
      <Header />
      <main className="legal-page">
        <section className="legal-page__hero">
          <p className="legal-page__eyebrow">Legal</p>
          <h1 className="legal-page__title">
            Privacy <span>Policy</span>
          </h1>
          <p className="legal-page__updated">Last updated: September 16, 2026</p>
        </section>

        <article className="legal-page__doc">
          <p>
            This Privacy Policy explains how Atheriam (&quot;Atheriam,&quot;
            &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) collects, uses,
            stores, and shares information when you visit atheriam.com, use our
            Layer 1 blockchain tools, interact with upcoming gaming products,
            explore tokenomics/ICO materials, or otherwise engage with the
            Atheriam ecosystem powered by $ATH.
          </p>

          <h2>1. Who we are</h2>
          <p>
            Atheriam is building an Agentic AI-powered Layer 1 blockchain for
            Web3 gaming. Our services may include the public website, whitepaper,
            tokenomics and ICO pages, wallet connection flows, staking or
            rewards interfaces (when available), community channels, and related
            product experiences.
          </p>

          <h2>2. Information we collect</h2>
          <h3>2.1 Information you provide</h3>
          <ul>
            <li>Contact details such as name, email, or social handles when you reach out to us</li>
            <li>Messages, support requests, feedback, or waitlist submissions</li>
            <li>Any information you choose to share in forms, surveys, or community programs</li>
          </ul>

          <h3>2.2 Wallet and on-chain information</h3>
          <ul>
            <li>Public wallet addresses when you connect a wallet</li>
            <li>Transaction hashes, interaction history, and other publicly available blockchain data</li>
            <li>
              Smart-contract interactions related to $ATH, staking, rewards,
              gameplay settlements, or ecosystem utilities when those features
              go live
            </li>
          </ul>
          <p>
            Blockchain data is public by nature. We do not control the
            permanence or visibility of on-chain records once they are confirmed.
          </p>

          <h3>2.3 Automatically collected information</h3>
          <ul>
            <li>Device, browser, and operating system details</li>
            <li>IP address, approximate location, referral source, and pages visited</li>
            <li>Cookies, local storage, analytics events, and similar technologies</li>
            <li>Performance and error logs that help us improve product stability</li>
          </ul>

          <h2>3. How we use information</h2>
          <ul>
            <li>Operate, maintain, and improve the Atheriam website and products</li>
            <li>Provide blockchain, gaming, tokenomics, and community experiences</li>
            <li>Process waitlists, announcements, ICO updates, and support requests</li>
            <li>Detect fraud, abuse, security incidents, and unauthorized access</li>
            <li>Analyze usage trends and optimize performance and content</li>
            <li>Comply with legal obligations and enforce our Terms of Service</li>
          </ul>

          <h2>4. Cookies and similar technologies</h2>
          <p>
            We may use essential cookies to keep the site functional, preference
            cookies to remember settings, and analytics cookies to understand how
            visitors use Atheriam. You can control cookies through your browser
            settings. Disabling certain cookies may limit site functionality.
          </p>

          <h2>5. How we share information</h2>
          <p>We do not sell your personal information. We may share data with:</p>
          <ul>
            <li>Service providers that help with hosting, analytics, email, security, or support</li>
            <li>Partners involved in verified ecosystem launches or integrations, where needed</li>
            <li>Professional advisors such as legal, tax, or compliance consultants</li>
            <li>Authorities when required by law, regulation, or valid legal process</li>
            <li>A successor entity in connection with a merger, acquisition, or restructuring</li>
          </ul>

          <h2>6. Blockchain and gaming considerations</h2>
          <p>
            Atheriam products may involve public smart contracts, play-to-earn
            mechanics, rewards, and digital assets. Wallet addresses and
            transaction activity can be linked to your activity across services.
            Do not share private keys, seed phrases, or recovery information with
            anyone, including Atheriam staff.
          </p>

          <h2>7. Data retention</h2>
          <p>
            We keep information only as long as needed for the purposes described
            in this Policy, unless a longer period is required by law, security,
            dispute resolution, or legitimate business needs.
          </p>

          <h2>8. Security</h2>
          <p>
            We use reasonable administrative, technical, and organizational
            safeguards to protect information. No method of transmission or
            storage is fully secure, and we cannot guarantee absolute security of
            online or on-chain systems.
          </p>

          <h2>9. Your choices and rights</h2>
          <p>
            Depending on your location, you may have rights to access, correct,
            delete, or restrict certain personal information, or to object to
            specific processing. Some requests may be limited by blockchain
            immutability or legal requirements. To exercise available rights,
            contact us using the details below.
          </p>

          <h2>10. Children</h2>
          <p>
            Atheriam services are not directed to children under 18 (or the age
            of digital consent in your jurisdiction). We do not knowingly collect
            personal information from children. If you believe a child has
            provided information, contact us so we can take appropriate action.
          </p>

          <h2>11. International transfers</h2>
          <p>
            Information may be processed in countries other than where you live.
            Where required, we take steps designed to protect transferred
            information in accordance with applicable law.
          </p>

          <h2>12. Changes to this Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. The &quot;Last
            updated&quot; date reflects the latest revision. Continued use of
            Atheriam after changes become effective means you acknowledge the
            updated Policy.
          </p>

          <h2>13. Contact</h2>
          <p>
            For privacy questions or requests, contact Atheriam via our
            community channels on{" "}
            <a
              href="https://x.com/AtheriamEco"
              target="_blank"
              rel="noreferrer"
            >
              X
            </a>{" "}
            or{" "}
            <a
              href="https://www.instagram.com/atheriamecosystem"
              target="_blank"
              rel="noreferrer"
            >
              Instagram
            </a>
            , or through official support channels listed on atheriam.com.
          </p>

          <div className="legal-page__links">
            <Link to="/terms">Terms of Service</Link>
            <Link to="/">Back to Home</Link>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
